import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SupportAction } from './SupportAction';
import styles from './AsyncState.module.css';
import { Button } from './Button';

export function LoadingState({label = 'Loading…'}: {label?: string}) {
  return <div className={styles.loading} role="status"><span className={styles.spinner} aria-hidden="true" />{label}<div className={styles.skeleton} aria-hidden="true" /></div>;
}
/**
 * `forbidden` (a 403, "no access"): trying again cannot help, so the "Try again" hint and button are left
 * out and the title alone says what to do. Off by default: every other error keeps both.
 */
export function ErrorState({title, onRetry, activeOperation = false, forbidden = false}: {
  title: string; onRetry?: () => unknown; activeOperation?: boolean; forbidden?: boolean;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [failedRetries, setFailedRetries] = useState(0);
  async function retry() {
    if (busy || !onRetry) return;
    setBusy(true);
    try {
      const result = await onRetry();
      // TanStack refetch resolves with an error result unless throwOnError is set.
      if (result && typeof result === 'object' && 'isError' in result && result.isError) setFailedRetries(n => n + 1);
    } catch { setFailedRetries(n => n + 1); }
    finally { setBusy(false); }
  }
  return <section className={styles.error} aria-label={title}>
    <span className={styles.errorIcon} aria-hidden="true">!</span>
    <h3>{title}</h3>{!forbidden && <p>{t('common.error.hint')}</p>}
    {onRetry && !forbidden && <Button variant="brand" className={styles.retry} busy={busy} onClick={() => void retry()}>{busy ? t('common.error.retrying') : t('common.error.retry')}</Button>}
    {(activeOperation || failedRetries > 0) && <SupportAction />}
  </section>;
}
export function StaleBanner({onRetry}: {onRetry?: () => unknown}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  async function retry() {
    if (busy || !onRetry) return;
    setBusy(true);
    try {
      const result = await onRetry();
      if (result && typeof result === 'object' && 'isError' in result && result.isError) setFailed(true);
    } catch { setFailed(true); }
    finally { setBusy(false); }
  }
  return <div className={styles.banner} role="status">{t('ui.asyncState.staleBanner')}
    {onRetry && <button disabled={busy} onClick={() => void retry()}>{busy ? t('common.error.retrying') : t('common.error.retry')}</button>}
    {failed && <SupportAction />}
  </div>;
}
